// src/components/UserInfoPanel.js

import React, { useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  FaUser,
  FaPencilAlt,
  FaCrown,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import { doc, setDoc } from "firebase/firestore";
import { db } from "../../config/firebase";
import { useDispatch } from "react-redux";
import { setLogin } from "../../store/slices/userSlice";
import { useNavigate } from "react-router-dom";

const UserInfoPanel = ({ userInfo }) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [username, setUsername] = useState(userInfo?.username || "");
  const [showProDetails, setShowProDetails] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const proFeatures = [
    t("settings.userInfo.proFeatures.overviewStats"),
    t("settings.userInfo.proFeatures.strategyRecommendations"),
    t("settings.userInfo.proFeatures.performanceAnalytics"),
    t("settings.userInfo.proFeatures.trendAnalysis"),
    t("settings.userInfo.proFeatures.practiceRoutines"),
    t("settings.userInfo.proFeatures.prioritySupport"),
  ];

  const handleUsernameUpdate = async () => {
    if (!userInfo?.uid) return;

    try {
      // Update username only in main user document
      await setDoc(
        doc(db, "userInfo", userInfo.uid),
        {
          username: username,
        },
        { merge: true }
      );

      dispatch(setLogin({ ...userInfo, username }));
      setIsEditing(false);
    } catch (err) {
      console.error(err);
      alert(t("settings.userInfo.failedToUpdateUsername"));
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-secondary/80 backdrop-blur-sm p-6 rounded-xl shadow-lg mb-6 border border-primary/20"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="flex items-center gap-4">
          {userInfo?.photoUrl ? (
            <img
              src={userInfo.photoUrl}
              alt="Profile"
              className="w-16 h-16 rounded-full object-cover border-2 border-test/20"
            />
          ) : (
            <div className="bg-test/20 p-3 rounded-full">
              <FaUser className="w-10 h-10 text-test" />
            </div>
          )}
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold text-test">
                {t("settings.userInfo.title")}
              </h2>
              {userInfo?.proMember && (
                <div className="flex items-center gap-1 bg-test/20 px-2 py-1 rounded-full">
                  <FaCrown className="w-4 h-4 text-test" />
                  <span className="text-sm text-test">Pro</span>
                </div>
              )}
            </div>
            {userInfo?.displayName && (
              <p className="text-white/80 text-sm mt-1">
                {userInfo.displayName}
              </p>
            )}
          </div>
        </div>
      </div>
      <div className="text-white text-sm space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-lightGray font-medium min-w-[100px]">
            {t("settings.userInfo.email")}:
          </span>
          <span>{userInfo.email}</span>
        </div>

        {(userInfo?.firstName || userInfo?.lastName) && (
          <>
            {userInfo.firstName && (
              <div className="flex items-center gap-2">
                <span className="text-lightGray font-medium min-w-[100px]">
                  {t("settings.userInfo.firstName")}:
                </span>
                <span>{userInfo.firstName}</span>
              </div>
            )}
            {userInfo.lastName && (
              <div className="flex items-center gap-2">
                <span className="text-lightGray font-medium min-w-[100px]">
                  {t("settings.userInfo.lastName")}:
                </span>
                <span>{userInfo.lastName}</span>
              </div>
            )}
          </>
        )}

        <div className="flex items-center gap-2">
          <span className="text-lightGray font-medium min-w-[100px]">
            {t("settings.userInfo.username")}:
          </span>
          {isEditing ? (
            <div className="flex items-center gap-2 flex-1">
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="flex-1 bg-primary border border-lightGray text-white rounded-lg px-2 py-1 focus:border-test focus:outline-none text-sm"
                placeholder="Enter username"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleUsernameUpdate();
                  } else if (e.key === "Escape") {
                    setIsEditing(false);
                    setUsername(userInfo?.username || "");
                  }
                }}
              />
              <button
                onClick={handleUsernameUpdate}
                className="px-3 py-1 bg-test text-primary rounded-md hover:bg-test/90 transition-colors duration-200 text-sm"
              >
                {t("settings.userInfo.save")}
              </button>
              <button
                onClick={() => {
                  setIsEditing(false);
                  setUsername(userInfo?.username || "");
                }}
                className="px-3 py-1 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors duration-200 text-sm"
              >
                {t("settings.userInfo.cancel")}
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span>{userInfo?.username || t("settings.userInfo.notSet")}</span>
              <button
                onClick={() => setIsEditing(true)}
                className="p-1 hover:bg-primary/50 rounded-full transition-colors duration-200"
                title={t("settings.userInfo.editUsername")}
              >
                <FaPencilAlt className="w-3.5 h-3.5 text-test" />
              </button>
            </div>
          )}
        </div>

        <div className="pt-2 border-t border-primary/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-lightGray font-medium min-w-[100px]">
                {t("settings.userInfo.membership")}:
              </span>
              <div className="flex items-center gap-2">
                <span className={userInfo?.proMember ? "text-test" : ""}>
                  {userInfo?.proMember
                    ? t("settings.userInfo.proMember")
                    : t("settings.userInfo.basicMember")}
                </span>
              </div>
            </div>
            <button
              onClick={() => setShowProDetails(!showProDetails)}
              className="flex items-center gap-1 text-test hover:text-test/80 transition-colors duration-200"
            >
              <span className="text-sm">
                {showProDetails
                  ? t("settings.userInfo.hideDetails")
                  : t("settings.userInfo.showDetails")}
              </span>
              {showProDetails ? (
                <FaChevronUp className="w-3 h-3" />
              ) : (
                <FaChevronDown className="w-3 h-3" />
              )}
            </button>
          </div>

          {showProDetails && (
            <div className="mt-4 bg-primary/30 rounded-lg p-4">
              {userInfo?.proMember ? (
                <>
                  <p className="text-test font-medium mb-2">
                    {t("settings.userInfo.proMemberThanks")}
                  </p>
                  <p className="text-white/80 mb-3">
                    {t("settings.userInfo.proMemberAccess")}
                  </p>
                  <ul className="space-y-2">
                    {proFeatures.map((feature, index) => (
                      <li
                        key={index}
                        className="flex items-center gap-2 text-white/90"
                      >
                        <FaCrown className="w-3 h-3 text-test flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <>
                  <p className="text-white/80 mb-3">
                    {t("settings.userInfo.upgradeToProMessage")}
                  </p>
                  <ul className="space-y-2 mb-4">
                    {proFeatures.map((feature, index) => (
                      <li
                        key={index}
                        className="flex items-center gap-2 text-white/60"
                      >
                        <FaCrown className="w-3 h-3 text-test/50 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default UserInfoPanel;
